// src/hooks/useSyncConflicts.ts

/**
 * useSyncConflicts - expose live sync conflicts and resolution to the UI.
 *
 * The cloud sync engine detects when a project diverged across devices and holds
 * the unresolved conflicts in its state. This hook subscribes to that state and
 * gives the UI a plain way to let a person choose a side. Nothing is overwritten
 * until they do ("No silent overwrites. Ever.").
 */

import { useEffect, useState } from "react";
import { cloudSync, type SyncConflict } from "@/sync/cloudSyncEngine";

export type ConflictSide = "mine" | "theirs";

export function useSyncConflicts() {
  const [conflicts, setConflicts] = useState<SyncConflict[]>([]);

  useEffect(() => {
    // onStateChange fires immediately with current state, then on every change.
    return cloudSync.onStateChange(state => {
      setConflicts(state.conflicts.filter(c => c.resolution === "pending"));
    });
  }, []);

  return {
    conflicts,
    hasConflicts: conflicts.length > 0,
    resolve: (id: string, side: ConflictSide) =>
      cloudSync.resolveConflict(id, side === "mine" ? "local_wins" : "cloud_wins"),
    resolveAll: (side: ConflictSide) =>
      cloudSync.resolveAllConflicts(side === "mine" ? "local_wins" : "cloud_wins"),
  };
}
